import { useEffect, useEffectEvent } from 'react';
import { getHotkeyHandler, getHotkeyMatcher, HotkeyItemOptions } from './parse-hotkey';

export type { HotkeyItemOptions };
export { getHotkeyHandler };

export type HotkeyItem = [string, (event: KeyboardEvent) => void, HotkeyItemOptions?];

function shouldFireEvent(
  event: KeyboardEvent,
  tagsToIgnore: string[],
  triggerOnContentEditable = false
) {
  if (event.target instanceof HTMLElement) {
    if (triggerOnContentEditable) {
      return !tagsToIgnore.includes(event.target.tagName);
    }

    return !event.target.isContentEditable && !tagsToIgnore.includes(event.target.tagName);
  }

  return true;
}

export function useHotkeys(
  hotkeys: HotkeyItem[],
  tagsToIgnore: string[] = ['INPUT', 'TEXTAREA', 'SELECT'],
  triggerOnContentEditable = false
) {
  const handleKeydown = useEffectEvent((event: KeyboardEvent) => {
    hotkeys.forEach(([hotkey, handler, options]) => {
      const { preventDefault = true, usePhysicalKeys = false } = options || {};

      if (
        getHotkeyMatcher(hotkey, usePhysicalKeys)(event) &&
        shouldFireEvent(event, tagsToIgnore, triggerOnContentEditable)
      ) {
        if (preventDefault) {
          event.preventDefault();
        }

        handler(event);
      }
    });
  });

  useEffect(() => {
    document.documentElement.addEventListener('keydown', handleKeydown);
    return () => document.documentElement.removeEventListener('keydown', handleKeydown);
  }, []);
}

export namespace useHotkeys {
  export type Hotkey = HotkeyItem;
}
